import * as classNames from 'classnames';
import isEqual from 'lodash-es/isEqual';
import { observer } from 'mobx-react';
import * as React from 'react';
import * as zenscroll from 'zenscroll';

// Styles
require('./OptionsList.css');
const c = 'comp-options-list-';

// Types
type TypeAlignOptions = 'default' | 'left' | 'center' | 'right';

export interface IOption {
    className?: string;
    focus?: boolean;
    value: any;
    label: string;
}

// Props
interface IOptionsListProps {
    className?: string | null;
    onChange?: ((changed: { value: any; label: string }) => void) | null;
    onChangeOnlyValue?: ((value: any) => void) | null;
    label?: string;
    value: any;
    options: IOption[];
    optionsListMaxHeight?: number;
    optionToHighlightIndex?: number | null;
    optionToHighlightClassName?: string | null;
    alignOptions?: TypeAlignOptions;
    loadingOptions?: boolean;
    loadingMessage?: string;
    noOptionsMessage?: string;
    highlightFirstOption?: boolean;
}

// Component
@observer
export class OptionsList extends React.Component<IOptionsListProps, {}> {
    static get defaultProps(): IOptionsListProps {
        return {
            className: null,
            onChange: null,
            onChangeOnlyValue: null,
            label: 'Results:',
            value: null,
            options: [],
            optionsListMaxHeight: 0,
            optionToHighlightIndex: null,
            optionToHighlightClassName: null,
            alignOptions: 'default',
            loadingOptions: false,
            loadingMessage: 'Loading...',
            noOptionsMessage: 'No options',
            highlightFirstOption: false,
        };
    }

    constructor() {
        super();

        this.initialScrollToSelectedValue();
    }

    public render() {
        // Options list class name
        let optionsListClassName = c + 'container';
        optionsListClassName += this.props.className
            ? ' ' + this.props.className
            : '';

        // Label text
        let labelText;
        let labelClassName = c + 'results-label';
        if (this.props.loadingOptions) {
            labelText = this.props.loadingMessage;
        } else if (
            this.props.options === null ||
            this.props.options.length === 0
        ) {
            labelText = this.props.noOptionsMessage;
            labelClassName += ' ' + c + 'results-empty-label';
        } else {
            labelText = this.props.label;
        }

        // Render options list container
        return (
            <div
                ref={this.referenceOptionListContainer}
                className={optionsListClassName}
            >
                <div className={c + 'results'}>
                    <p className={labelClassName}>{labelText}</p>

                    <ul
                        className={classNames({
                            [c + 'align-' + this.props.alignOptions]: true,
                        })}
                        style={{
                            maxHeight: this.props.optionsListMaxHeight
                                ? this.props.optionsListMaxHeight + 'px'
                                : undefined,
                        }}
                    >
                        {this.props.options.map((option, optionIndex) => {
                            // Check if result is active
                            let resultIsActive = false;
                            if (this.props.value !== null) {
                                if (
                                    typeof option.value === 'object' &&
                                    option.value &&
                                    typeof option.value.length !== 'undefined'
                                ) {
                                    resultIsActive = option.value.some(
                                        (val: any) => {
                                            return this.props.value === val
                                                ? true
                                                : false;
                                        }
                                    );
                                } else {
                                    resultIsActive = isEqual(
                                        option.value,
                                        this.props.value
                                    );
                                }
                            } else {
                                resultIsActive =
                                    option.value === this.props.value;
                            }

                            // Check if result is focused on
                            const resultIsInFocus =
                                (typeof option.focus !== 'undefined' &&
                                    option.focus) ||
                                (this.props.optionToHighlightIndex !== null &&
                                    this.props.optionToHighlightIndex ===
                                        optionIndex)
                                    ? true
                                    : false;

                            // Render result
                            return (
                                <li
                                    ref={
                                        resultIsActive
                                            ? this.referenceActiveListItem
                                            : undefined
                                    }
                                    key={
                                        typeof option.value === 'string'
                                            ? option.value + optionIndex
                                            : JSON.stringify(option.value) +
                                              optionIndex
                                    }
                                    className={classNames(option.className, {
                                        [c + 'result-active']: resultIsActive,
                                        [c +
                                        'result-highlight']: resultIsInFocus,
                                        [this.props
                                            .optionToHighlightClassName]: resultIsInFocus,
                                    })}
                                >
                                    <button
                                        onClick={this.handleOptionClick(
                                            option.value,
                                            option.label
                                        )}
                                    >
                                        {typeof option.label !== 'undefined' &&
                                        option.label !== null
                                            ? option.label
                                            : option.value}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        );
    }

    private initialScrollToSelectedValue = () => {
        if (this.containerScroller === null) {
            if (this.optionListContainer) {
                const parent = this.optionListContainer.parentElement;
                if (parent) {
                    this.containerScroller = zenscroll.createScroller(
                        parent,
                        100,
                        128
                    );

                    if (this.activeListItem) {
                        this.containerScroller.intoView(this.activeListItem);
                    }
                }
            } else {
                setTimeout(() => {
                    this.initialScrollToSelectedValue();
                }, 64);
            }
        }
    };

    private containerScroller = null;

    private activeListItem: HTMLLIElement = null;
    private optionListContainer: HTMLDivElement = null;
    private referenceActiveListItem = ref => (this.activeListItem = ref);
    private referenceOptionListContainer = ref =>
        (this.optionListContainer = ref);

    private handleOptionClick = (value: any, label: string) => (
        e: React.MouseEvent<HTMLButtonElement>
    ) => {
        if (this.props.onChange) {
            this.props.onChange({ value, label });
        }

        if (this.props.onChangeOnlyValue) {
            this.props.onChangeOnlyValue(value);
        }
    };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/list/OptionsList.tsx